<script setup>
import { ref, computed, inject, onMounted, watch } from 'vue';
import * as navConfig from '@/navConfig';
import { router } from '@inertiajs/vue3';
import { useDisplay } from 'vuetify';
import { useUserStore } from '@/stores/user';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';
import { isRole } from '@/utils/roleHelper';
import { loadLanguageAsync } from 'laravel-vue-i18n';
import { useAccessStore } from '@/stores/access';

defineOptions({
    inheritAttrs: false
});

const drawer = ref(true);
const rail = ref(false);
const { thresholds, mobile } = useDisplay();

const { profile, lastLogin, locale } = storeToRefs(useUserStore());
const { securityCheckpoint } = storeToRefs(useOptionsStore());

const { stopAccessMember } = useAccessStore();

const isAccessingMember = computed(() => !!profile.value?.is_accessing_member);

const isAccessing = computed(() => (
    isAccessingMember.value
));

const roles = computed(() => isRole(profile.value));

const isLoading = inject('isLoading');

const routes = computed(() => {
    let allRoutes = [];
    if (isAccessingMember.value) {
        allRoutes.push(...navConfig.accessMember());
    } else if (roles.value.superAdmin()) {
        allRoutes.push(...navConfig.superAdmin());
    } else if (roles.value.admin()) {
        allRoutes.push(...navConfig.admin());
    } else if (roles.value.member()) {
        allRoutes.push(...navConfig.member());
    }

    const uniqueRoutes = allRoutes.reduce((acc, current) => {
        const x = acc.find(item => item.name === current.name);
        return x ? acc : [...acc, current];
    }, []);

    return uniqueRoutes;
});

const bottomNavRoutes = computed(() => {
    if (roles.value.superAdmin()) {
        return navConfig.bottomNavRoutes();
    } else {
        return navConfig.bottomNavRoutes().filter(x => !x.private);
    }
});

const isLocal = computed(() => {
    return import.meta.env.VITE_ENV === 'local';
});

const stopAccessing = async () => {
    if (isAccessingMember.value) {
        const res = await stopAccessMember();
        if (res.data?.success) {
            router.visit(res.data.redirect);
        }
    }
};

onMounted(() => {
    if (window.innerWidth <= thresholds.value.md) {
        drawer.value = false;
    }
});

const logout = async () => {
    await router.post('/logout');
};

const changeBar = () => {
    if (window.innerWidth <= thresholds.value.md) {
        rail.value = false;
    }
    drawer.value = !drawer.value;
};

const activeRoute = ref(null);

const visitPath = (route) => {
    activeRoute.value = route.path;
    if (route.private) {
        window.open(route.path, '_blank');
    } else {
        router.visit(route.path);
    }
    if (window.innerWidth <= thresholds.value.md) {
        drawer.value = false;
    }
};

const { setLocale } = useUserStore();
watch(locale, (lang) => {
    loadLanguageAsync(lang);
});

const hideLocaleToggle = true;

</script>

<template>
<div>
    <v-app>
        <div
            v-if="isAccessing && !securityCheckpoint && mobile"
            class="fixed top-0 left-0 right-0 bg-primary text-white px-6 py-3 flex justify-between items-center shadow-md z-[1010]">
            <div class="flex items-center gap-2.5">
                <v-icon 
                    icon="mdi-account-eye" 
                    size="18" />
                <span class="text-sm md:text-base">
                    {{ $t('label.accessing') }} 
                    <span class="font-semibold">{{ profile.first_name }} {{ profile.last_name }}</span>
                </span>
            </div>
            <v-btn
                size="small"
                variant="outlined"
                color="white"
                class="!h-8 !text-xs !px-4 hover:bg-white/10"
                @click="stopAccessing">
                {{ $t('label.exit') }}
            </v-btn>
        </div>

        <v-navigation-drawer
            v-if="!securityCheckpoint"
            v-model="drawer"
            :rail="rail"
            mobile-breakpoint="md"
            :class="isAccessing && mobile ? 'mt-[48px]' : ''"
            app>
            <div
                v-if="isAccessingMember"
                class="border rounded-lg text-center m-4 p-2 cursor-pointer shadow bg-primary hover:bg-primary-light text-white"
                @click="stopAccessing">
                <div class="text-base">
                    {{ profile.first_name }} {{ profile.last_name }}
                </div>
                <div class="text-xs italic">
                    {{ $t('label.click_here_to_stop_access') || 'Click to stop access' }}
                </div>
            </div>

            <v-list>
                <img
                    :src="rail ? '/images/icon-logo.png' : '/images/logo.png'"
                    :class="rail 
                        ? 'mx-auto px-2 pt-1.5 h-10 object-contain' 
                        : 'w-full px-4 py-3 h-14 object-contain'"
                    alt="Logo"
                    class="cursor-pointer"
                    @click="$inertia.visit('/')">
            </v-list>

            <v-list class="nav">
                <div class="hidden md:flex" :class="!rail ? 'justify-end' : 'justify-center'">
                    <v-app-bar-nav-icon
                        v-tooltip.right="rail ? 'Open' : 'Minimize'"
                        color="accent"
                        :icon="!rail ? 'mdi-arrow-collapse-left' : 'mdi-arrow-collapse-right'"
                        @click="rail = !rail" />
                </div>
                <v-list-item
                    v-for="route in routes"
                    :key="route.name"
                    v-tooltip.right="!drawer ? '' : route.label"
                    :class="{'v-list-item--active': route.name === $page.component}"
                    :variant="route.name === $page.component ? 'tonal' : 'text'"
                    :title="route.label"
                    :value="route.name"
                    @click="visitPath(route)">
                    <template #prepend>
                        <v-progress-circular
                            v-if="isLoading && route.path === activeRoute"
                            :width="3"
                            :size="24"
                            class="mr-8"
                            color="secondary"
                            indeterminate />
                        <v-icon v-else-if="activeRoute === route.path" :icon="route.icon" color="secondary" />
                        <v-icon v-else :icon="route.icon" />
                    </template>
                </v-list-item>
            </v-list>

            <template v-if="!securityCheckpoint" #append>
                <div class="border-t border-grey-500">
                    <v-list>
                        <v-list-item
                            v-for="route in bottomNavRoutes"
                            :key="route.name"
                            v-tooltip.right="!drawer ? '' : route.label"
                            :class="{'v-list-item--active': route.name === $page.component}"
                            :variant="route.name === $page.component ? 'tonal' : 'text'"
                            :title="route.label"
                            :value="route.name"
                            @click="visitPath(route)">
                            <template #prepend>
                                <v-progress-circular
                                    v-if="isLoading && route.path === activeRoute"
                                    :width="3"
                                    :size="24"
                                    class="mr-8"
                                    color="secondary"
                                    indeterminate />
                                <v-icon v-else-if="activeRoute === route.path" :icon="route.icon" color="secondary" />
                                <v-icon v-else :icon="route.icon" />
                            </template>
                        </v-list-item>

                        <v-list-item
                            v-if="isLocal"
                            v-tooltip.right="!drawer ? '' : 'Project Setup'"
                            prepend-icon="mdi-magnify"
                            title="Project Setup"
                            @click.prevent="$inertia.visit('/local/project')">
                            <template #prepend>
                                <v-progress-circular
                                    v-if="isLoading && activeRoute === '/local/project'"
                                    :width="3"
                                    :size="24"
                                    class="mr-8"
                                    color="secondary"
                                    indeterminate />
                                <v-icon v-else-if="$page.component === 'Local/ProjectSetup'" icon="mdi-magnify" color="secondary" />
                                <v-icon v-else icon="mdi-magnify" />
                            </template>
                        </v-list-item>
                        <v-list-item
                            v-tooltip.right="!drawer ? '' : 'Sign Out'"
                            prepend-icon="mdi-power-standby"
                            title="Sign Out"
                            value="logout"
                            @click.prevent="logout">
                            <template #prepend>
                                <v-icon icon="mdi-power-standby" />
                            </template>
                        </v-list-item>
                    </v-list>
                </div>
            </template>
        </v-navigation-drawer>

        <v-app-bar 
            v-if="!securityCheckpoint" 
            app
            :class="isAccessing && mobile? 'mt-[48px]' : ''">
            <div class="block md:hidden ml-4">
                <v-app-bar-nav-icon @click="changeBar" />
            </div>
            <div class="flex justify-items-end justify-end w-full mr-6">
                <img
                    class="md:hidden block h-10 object-contain"
                    src="/images/logo.png"
                    alt="Logo">
            </div>
        </v-app-bar>

        <v-main
            app 
            :class="isAccessingMember && mobile ? 'mt-[48px]' : ''">
            <v-container fluid>
                <slot />
            </v-container>
        </v-main>

        <div class="z-[1008]">
            <modals-container />
        </div>

        <v-bottom-navigation
            v-if="!securityCheckpoint && mobile"
            :model-value="$page.component"
            grow
            fixed
            elevation="8"
            class="bottom-nav">
            <v-btn
                v-for="route in routes"
                :key="route.name"
                :value="route.name"
                :active="route.name === $page.component"
                :ripple="true"
                height="64"
                class="nav-btn"
                @click="visitPath(route)">
                <div class="nav-btn-content">
                    <v-icon 
                        :size="route.name === $page.component ? 26 : 24"
                        :class="route.name === $page.component ? 'active-icon' : ''">
                        {{ route.icon }}
                    </v-icon>
                    <span 
                        :class="[
                            'nav-label',
                            route.name === $page.component ? 'active-label font-semibold' : 'font-normal'
                        ]">
                        {{ route.label }}
                    </span>
                </div>
            </v-btn>
        </v-bottom-navigation>

        <v-footer v-if="!securityCheckpoint && !mobile" app>
            <div class="mb-0 flex justify-between w-full items-center mx-4 text-sm">
                <div class="italic">{{ $t('auth.last-login', { 'lastLogin': lastLogin }) }}</div>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <div>Copyright &copy; Fabled Solutions {{ new Date().getFullYear() }}</div>
                <v-btn-toggle
                    v-if="!hideLocaleToggle"
                    :model-value="locale"
                    class="!h-6 min-w-20 ml-2"
                    variant="outlined"
                    color="primary"
                    mandatory
                    group
                    @update:model-value="setLocale">
                    <v-btn value="en">{{ $t('common.en') }}</v-btn>
                    <v-btn value="fr">{{ $t('common.fr') }}</v-btn>
                </v-btn-toggle>
            </div>
        </v-footer>
    </v-app>
</div>
</template>
